// Rolls the per-task master-solution / grading-scheme provenance up to one value per exam, and keeps that
// value on the exam state.
//
// Why a denormalized copy exists: the per-task `source` on `MasterSolution` / `GradingScheme` is the
// authority, but the status bar renders from `examState` alone and never loads the task tree. Rather than
// make a lightweight status component fetch every task, the roll-up is stored on the state and refreshed
// from each path that writes a solution — the extraction steps and the two generation agents.

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { SOLUTION_SOURCES, type SolutionSource, type SolutionSourceState } from '../../shared/solution-source.js';
import { Exam } from '../exam.js';
import { collectAnswerableTasks } from './solution-completeness.js';

/** Embedded sections are stored plain, but some read paths wrap them as `{ doc }`. */
function unwrap(value: any): any {
  return value?.doc ?? value;
}

/**
 * The stored source of one section, or null when the section carries no content at all.
 *
 * Content written before this field existed has no `source`, so it is inferred: a bounding box can only come
 * from an extraction of an uploaded document (generation writes text and never boxes), and text without any
 * box is what generation produces. Everything written since carries the field explicitly, which is why the
 * teacher's own canvas input is stamped 'manual' at the point of writing rather than left to this guess.
 */
function sectionSource(section: any, contentField: 'content' | 'criteria'): SolutionSource | null {
  const s = unwrap(section);
  if (!s) return null;

  const boxes = s.boundingBoxes;
  const hasBoxes = Array.isArray(boxes) && boxes.length > 0;
  const text = s[contentField];
  const hasText = typeof text === 'string' && text.trim().length > 0;
  if (!hasBoxes && !hasText) return null;

  if (SOLUTION_SOURCES.includes(s.source)) return s.source as SolutionSource;
  return hasBoxes ? 'upload' : 'generated';
}

/** The single source every task with content agrees on, 'mixed' when they disagree, null when none has any. */
function rollUp(sources: Array<SolutionSource | null>): SolutionSourceState | null {
  const present = sources.filter((source): source is SolutionSource => source !== null);
  if (present.length === 0) return null;
  const first = present[0] as SolutionSource;
  return present.every(source => source === first) ? first : 'mixed';
}

export interface SourceCounts {
  upload: number;
  generated: number;
  manual: number;
}

export interface SolutionSourceSummary {
  masterSolution: SolutionSourceState | null;
  gradingScheme: SolutionSourceState | null;
  /** Per-section counts, for a status text that wants to say "8 uploaded, 2 generated". */
  counts: {
    masterSolution: SourceCounts;
    gradingScheme: SourceCounts;
  };
}

export function summarizeSolutionSources(tasks: any[] | null | undefined): SolutionSourceSummary {
  const answerable = collectAnswerableTasks(tasks as any);
  const msSources = answerable.map(task => sectionSource(task.masterSolution, 'content'));
  const gsSources = answerable.map(task => sectionSource(task.gradingScheme, 'criteria'));
  const count = (sources: Array<SolutionSource | null>): SourceCounts => ({
    upload: sources.filter(source => source === 'upload').length,
    generated: sources.filter(source => source === 'generated').length,
    manual: sources.filter(source => source === 'manual').length,
  });

  return {
    masterSolution: rollUp(msSources),
    gradingScheme: rollUp(gsSources),
    counts: { masterSolution: count(msSources), gradingScheme: count(gsSources) },
  };
}

/**
 * German status text for the agent-facing status blocks. Returns null when the section holds nothing yet, so
 * the caller falls back to its progress-based wording ("In Bearbeitung", "Noch nicht vorhanden", …).
 *
 * `counts` is optional because not every caller has the task tree: with it the mixed case can say how the
 * split falls, without it it just names the state.
 */
export function describeSolutionSourceState(
  sourceState: SolutionSourceState | null | undefined,
  counts?: SourceCounts,
): string | null {
  if (sourceState === 'upload') return 'Hochgeladen';
  if (sourceState === 'generated') return 'Generiert';
  if (sourceState === 'manual') return 'Vom Nutzer selbst erstellt';
  if (sourceState !== 'mixed') return null;

  if (!counts) return 'Gemischt (teils hochgeladen, teils generiert)';

  const parts = [
    counts.upload > 0 ? `${counts.upload} hochgeladen` : '',
    counts.generated > 0 ? `${counts.generated} generiert` : '',
    counts.manual > 0 ? `${counts.manual} selbst erstellt` : '',
  ].filter(Boolean);
  return `Gemischt (${parts.join(', ')})`;
}

/**
 * Recomputes the roll-up from the exam's persisted tasks and stores it on the exam state.
 *
 * Reads the database rather than taking a task array, so a caller that has just persisted its own tree
 * cannot store a summary of a stale one. Never throws: a failed refresh degrades the status label, and
 * that must not take an extraction or a generation down with it.
 */
export async function refreshSolutionSourceState(examId: string): Promise<void> {
  if (!examId) return;

  try {
    const examCollection = await getCollection(Exam as any);
    const exam = await examCollection.findOne({ _id: examId } as any, { projection: { tasks: 1 } } as any);
    const summary = summarizeSolutionSources((exam as any)?.tasks);

    await examCollection.updateOne(
      { _id: examId } as any,
      {
        $set: {
          'examState.masterSolutionSourceState': summary.masterSolution,
          'examState.gradingSchemeSourceState': summary.gradingScheme,
        },
      } as any,
    );
  } catch (err) {
    logger.error({ err, examId }, 'Failed to refresh the solution source state for exam');
  }
}
